import React, { useState } from 'react';
import { X, CheckCircle2, XCircle, AlertCircle, Loader2, Save, MessageSquare, IndianRupee } from 'lucide-react';
import styles from './AdvanceRequests.module.css';
import toast from 'react-hot-toast';
import api from '../../../api/axios';

const ApprovalModal = ({ isOpen, onClose, record, onSuccess }) => {
  const [status, setStatus] = useState('approved'); // approved, partially_approved, rejected
  const [remark, setRemark] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!remark) {
      toast.error('Please provide a remark');
      return;
    }
    if (status === 'partially_approved' && (!approvedAmount || parseFloat(approvedAmount) <= 0)) {
      toast.error('Please enter a valid approved amount');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        status: status,
        approved_remark: remark
      };

      if (status === 'partially_approved') {
        payload.approved_amount = approvedAmount;
      }

      const response = await api.post(`/advance-requests/${record.id}/approve`, payload);
      if (response.data.success) {
        toast.success(`Request ${status.replace('_', ' ')} successfully`);
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.message || 'Failed to process approval');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !record) return null;

  return (
    <div className={styles['modal-backdrop']} onClick={onClose}>
      <div className={styles['modal-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['modal-header']}>
          <div className={styles['modal-title-group']}>
            <h2>Process Advance Request</h2>
            <p>Review and decide on the application for <strong>{record.employee_name || record.employee?.name}</strong></p>
          </div>
          <button className={styles['close-icon-btn']} onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className={styles['modal-form']}>
          <div className={styles['status-selector-grid']}>
            <button 
              type="button" 
              className={`${styles['status-option']} ${status === 'approved' ? styles['active-approved'] : ''}`}
              onClick={() => { setStatus('approved'); setApprovedAmount(record.requested_amount); }}
            >
              <CheckCircle2 size={24} />
              <span>Full Approval</span>
            </button>
            <button 
              type="button" 
              className={`${styles['status-option']} ${status === 'partially_approved' ? styles['active-partial'] : ''}`}
              onClick={() => setStatus('partially_approved')}
            >
              <AlertCircle size={24} />
              <span>Partial Approval</span>
            </button>
            <button 
              type="button" 
              className={`${styles['status-option']} ${status === 'rejected' ? styles['active-rejected'] : ''}`}
              onClick={() => setStatus('rejected')}
            >
              <XCircle size={24} />
              <span>Reject Request</span>
            </button>
          </div>

          <div className={styles['modal-body']}>
            <div className={styles['requested-info-box']}>
              <div className={styles['info-item']}>
                <label>Requested Amount</label>
                <span className={styles['val-req']}>₹{parseFloat(record.requested_amount).toLocaleString('en-IN')}</span>
              </div>
              {status === 'partially_approved' && (
                <div className={styles['info-item']}>
                  <label>Approved Amount</label>
                  <div className={styles['amount-input-wrapper']}>
                    <IndianRupee size={16} />
                    <input 
                      type="number" 
                      max={record.requested_amount}
                      value={approvedAmount}
                      onChange={(e) => setApprovedAmount(e.target.value)}
                      placeholder="Enter amount"
                      autoFocus
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={styles['form-group']}>
              <label><MessageSquare size={16} /> Approval Remark / Reason</label>
              <textarea 
                className={styles['modal-textarea']}
                placeholder="Provide a reason for your decision..."
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows="4"
              />
            </div>
          </div>

          <div className={styles['modal-footer']}>
            <button type="button" className={styles['btn-cancel']} onClick={onClose}>Cancel</button>
            <button type="submit" className={styles['btn-submit']} disabled={loading}>
              {loading ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
              <span>Confirm {status.replace('_', ' ').toUpperCase()}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApprovalModal;
